import { useCallback, useState } from 'react'
import { Ban, Bell, BellOff, Pencil, Radio, RotateCcw, Square, Trash2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Drawer, DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import {
  cancelCoverageEvent, deleteCoverageEvent, endCoverageEvent, getCoverageEvent,
  reopenCoverageEvent, unwatchEvent, updateCoverageEvent, watchEvent,
} from './api'
import { EditEventDialog } from './EditEventDialog'
import { FeedsPanel } from './panels/FeedsPanel'
import { AssignmentsPanel } from './panels/AssignmentsPanel'
import { MarksPanel } from './panels/MarksPanel'
import { RecordingsPanel } from './panels/RecordingsPanel'
import {
  classificationLabel, eventStatusLabel, eventTypeLabel, isSensitive, placeOf,
} from './vocab'
import { CoverageTranscriptPanel } from './TranscriptPanel'
import { explain } from '@/shared/api/problem'
import type { CoverageEventResponse } from '@/shared/api/types'

const TABS = [
  { id: 'cameras', label: 'Cameras' },
  { id: 'recordings', label: 'Archive' },
  { id: 'marks', label: 'Moments' },
  // Next to Archive on purpose: a transcript is the recording in another form,
  // and somebody looking for a sentence reaches for the same place they reach
  // for the footage.
  { id: 'transcript', label: 'Transcript' },
  { id: 'assignments', label: 'Who is filming' },
] as const

type TabId = typeof TABS[number]['id']

export function CoverageEventDrawer({
  eventId, onClose, onChanged,
}: { eventId: string; onClose: () => void; onChanged?: () => void }) {
  const [tab, setTab] = useState<TabId>('cameras')
  const [tick, setTick] = useState(0)
  const [actionError, setActionError] = useState<string | null>(null)
  const canUpdate = useCan('coverage:update')
  const canDelete = useCan('coverage:delete')

  const { data: event, error, loading } = useLoad(() => getCoverageEvent(eventId), [eventId, tick])
  const reload = useCallback(() => { setTick((t) => t + 1); onChanged?.() }, [onChanged])

  const live = (event?.feeds ?? []).some((f) => f.statusCode === 'LIVE')

  return (
    <Drawer
      open
      onClose={onClose}
      width="lg"
      title={event?.title ?? 'Coverage event'}
      subtitle={event ? `${event.ref} · ${eventTypeLabel(event.eventTypeCode)} · ${placeOf(event)}` : undefined}
      right={event ? (
        <EventActions
          event={event}
          live={live}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onDone={reload}
          onError={setActionError}
        />
      ) : undefined}
    >
      <ErrorStrip message={error ?? actionError} />

      {!event ? (loading ? <LoadingBlock label="Loading event…" /> : null) : (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-1.5 text-erp-xs">
            <span className={cn(
              'rounded px-1.5 py-0.5 font-medium',
              live ? 'bg-red-600 text-white'
                : event.statusCode === 'SCHEDULED' ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                : 'bg-muted text-muted-foreground',
            )}>
              {live ? 'Live' : eventStatusLabel(event.statusCode)}
            </span>
            <span className={cn(
              'rounded px-1.5 py-0.5 font-medium',
              isSensitive(event.classificationCode)
                ? 'bg-amber-500/15 text-amber-700 dark:text-amber-400'
                : 'bg-muted text-muted-foreground',
            )}>
              {classificationLabel(event.classificationCode)}
            </span>
            <span className="text-muted-foreground">
              {new Date(event.scheduledStart).toLocaleString()}
            </span>
            {event.organiserName && (
              <span className="text-muted-foreground">· organised by {event.organiserName}</span>
            )}
          </div>

          {event.subject && (
            <div className="mb-3">
              <Note tone="info">
                {event.subject.resolved
                  ? <>Coverage of <strong>{event.subject.label}</strong>
                    {event.subject.secondaryLabel ? ` · ${event.subject.secondaryLabel}` : ''}.</>
                  : <>Attached to a {event.subject.subjectTypeCode.toLowerCase()} this build cannot
                    name — the module may be switched off, or the record removed. The link is
                    kept either way.</>}
              </Note>
            </div>
          )}

          <div className="mb-3 flex flex-wrap gap-1 border-b border-border pb-2" role="tablist"
            aria-label="Coverage event detail">
            {TABS.map((t, i) => (
              <button
                key={t.id}
                type="button"
                role="tab"
                id={`coverage-tab-${t.id}`}
                aria-controls={tab === t.id ? 'coverage-panel' : undefined}
                aria-selected={tab === t.id}
                tabIndex={tab === t.id ? 0 : -1}
                onKeyDown={(e) => {
                  if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return
                  e.preventDefault()
                  const step = e.key === 'ArrowRight' ? 1 : -1
                  const next = TABS[(i + step + TABS.length) % TABS.length]!
                  setTab(next.id)
                  document.getElementById(`coverage-tab-${next.id}`)?.focus()
                }}
                className={cn(
                  'rounded-md px-2.5 py-1 text-erp-xs font-medium',
                  tab === t.id ? 'bg-chocolate-soft/60 text-chocolate' : 'text-muted-foreground hover:bg-muted',
                )}
                onClick={() => setTab(t.id)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div role="tabpanel" id="coverage-panel" tabIndex={0}
            aria-labelledby={`coverage-tab-${tab}`} className="focus:outline-none">
            {tab === 'cameras' && <FeedsPanel event={event} onChanged={reload} />}
            {tab === 'recordings' && <RecordingsPanel event={event} onChanged={reload} />}
            {tab === 'marks' && <MarksPanel event={event} />}
            {tab === 'transcript' && <CoverageTranscriptPanel eventId={eventId} />}
            {tab === 'assignments' && <AssignmentsPanel event={event} onChanged={reload} />}
          </div>

          {event.description && (
            <DrawerSection title="Notes">
              <p className="whitespace-pre-wrap text-erp-sm text-muted-foreground">{event.description}</p>
            </DrawerSection>
          )}
        </>
      )}
    </Drawer>
  )
}

function EventActions({
  event, live, canUpdate, canDelete, onDone, onError,
}: {
  event: CoverageEventResponse
  live: boolean
  canUpdate: boolean
  canDelete: boolean
  onDone: () => void
  onError: (m: string | null) => void
}) {
  const [cancelling, setCancelling] = useState(false)
  const [reopening, setReopening] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [editing, setEditing] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  /**
   * Optimistic, and unrecoverable-by-design: there is no endpoint that reports
   * whether the caller is watching, so this reflects what they have done in this
   * session and nothing else. Better than a control that lies about server state
   * it cannot read — and stated here so nobody "fixes" it by inventing a GET.
   */
  const [watched, setWatched] = useState<boolean | null>(null)

  const over = event.statusCode === 'ENDED' || event.statusCode === 'CANCELLED'
  const neverStarted = event.statusCode === 'SCHEDULED' && event.feeds.every((f) => !f.startedAt)

  return (
    <div className="flex items-center gap-1.5">
      {/*
        Undo. Ending is the one destructive control somebody presses while an
        event is actually happening, and a thumb on the wrong button used to end
        the coverage with no way back. Reopening moves the status and touches
        nothing else — every recording, view and mark survives.
      */}
      {canUpdate && over && (
        <button type="button" className="erp-btn h-8" disabled={busy}
          onClick={() => setReopening(true)}>
          <RotateCcw className="h-3.5 w-3.5" /> Reopen
        </button>
      )}
      {!over && (
        <button
          type="button"
          className="erp-btn h-8"
          aria-label={watched ? 'Stop watching this event' : 'Tell me when this goes live'}
          title={watched ? 'You will not be told when this goes live' : 'Tell me when this goes live'}
          disabled={busy}
          onClick={async () => {
            setBusy(true); onError(null)
            try {
              if (watched) { await unwatchEvent(event.id); setWatched(false) }
              else { await watchEvent(event.id); setWatched(true) }
            } catch (e) { onError(explain(e)) } finally { setBusy(false) }
          }}
        >
          {watched ? <BellOff className="h-3.5 w-3.5" /> : <Bell className="h-3.5 w-3.5" />}
        </button>
      )}
      {canUpdate && !over && (
        <button type="button" className="erp-btn h-8" onClick={() => setEditing(true)}>
          <Pencil className="h-3.5 w-3.5" /> Edit
        </button>
      )}
      {canDelete && neverStarted && (
        <button type="button" className="erp-btn h-8" aria-label="Delete this event"
          onClick={() => setDeleting(true)}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
      {canUpdate && !over && event.statusCode === 'SCHEDULED' && (
        <button type="button" className="erp-btn h-8" disabled={live || busy}
          title={live ? 'A camera is on air — end the event instead' : undefined}
          onClick={() => setCancelling(true)}>
          <Ban className="h-3.5 w-3.5" /> Cancel
        </button>
      )}
      {canUpdate && !over && (
        <button
          type="button"
          className="erp-btn h-8"
          disabled={busy}
          onClick={async () => {
            setBusy(true); onError(null)
            try { await endCoverageEvent(event.id); onDone() }
            catch (e) { onError(explain(e)) } finally { setBusy(false) }
          }}
        >
          {live ? <Square className="h-3.5 w-3.5" /> : <Radio className="h-3.5 w-3.5" />} End
        </button>
      )}

      {editing && (
        <EditEventDialog
          event={event}
          onClose={() => setEditing(false)}
          onSaved={async (patch) => {
            await updateCoverageEvent(event.id, patch)
            setEditing(false)
            onDone()
          }}
        />
      )}

      {reopening && (
        <Modal open onClose={() => setReopening(false)} title="Reopen this event">
          <div className="space-y-3">
            <p className="text-erp-sm text-muted-foreground">
              The event goes back to scheduled and its cameras can go on air again. Nothing
              recorded is touched — a second broadcast on a camera becomes a second recording
              beside the first, not more bytes appended to it.
            </p>
            <label className="block space-y-1">
              <span className="text-erp-xs font-medium">Why</span>
              <input className="erp-input w-full" value={reason} autoFocus
                onChange={(e) => setReason(e.target.value)}
                placeholder="Ended by mistake / the rally resumed" />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="erp-btn" onClick={() => setReopening(false)}>
                Leave it closed
              </button>
              <button
                type="button"
                className="erp-btn erp-btn-primary"
                disabled={!reason.trim() || busy}
                onClick={async () => {
                  setBusy(true); onError(null)
                  try { await reopenCoverageEvent(event.id, reason.trim()); setReopening(false); onDone() }
                  catch (e) { onError(explain(e)) } finally { setBusy(false) }
                }}
              >
                Reopen
              </button>
            </div>
          </div>
        </Modal>
      )}

      {deleting && (
        <Modal open onClose={() => setDeleting(false)} title="Delete this event">
          <div className="space-y-3">
            <p className="text-erp-sm text-muted-foreground">
              Only possible because no camera has ever been on air here. Anything that was
              filmed is ended, never deleted.
            </p>
            <label className="block space-y-1">
              <span className="text-erp-xs font-medium">Reason</span>
              <input className="erp-input w-full" value={reason} autoFocus
                onChange={(e) => setReason(e.target.value)} />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="erp-btn" onClick={() => setDeleting(false)}>Keep it</button>
              <button
                type="button"
                className="erp-btn erp-btn-primary"
                disabled={!reason.trim() || busy}
                onClick={async () => {
                  setBusy(true); onError(null)
                  try { await deleteCoverageEvent(event.id, reason.trim()); setDeleting(false); onDone() }
                  catch (e) { onError(explain(e)) } finally { setBusy(false) }
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </Modal>
      )}

      {cancelling && (
        <Modal open onClose={() => setCancelling(false)} title="Cancel this event">
          <div className="space-y-3">
            <p className="text-erp-sm text-muted-foreground">
              A cancelled event stays on the diary. Nothing is deleted, and no camera can be
              taken live on it afterwards.
            </p>
            <label className="block space-y-1">
              <span className="text-erp-xs font-medium">Reason</span>
              <input className="erp-input w-full" value={reason}
                onChange={(e) => setReason(e.target.value)} placeholder="Recorded in the audit log" />
            </label>
            <div className="flex justify-end gap-2">
              <button type="button" className="erp-btn" onClick={() => setCancelling(false)}>Keep it</button>
              <button
                type="button"
                className="erp-btn erp-btn-primary"
                disabled={!reason.trim() || busy}
                onClick={async () => {
                  setBusy(true); onError(null)
                  try { await cancelCoverageEvent(event.id, reason.trim()); setCancelling(false); onDone() }
                  catch (e) { onError(explain(e)) } finally { setBusy(false) }
                }}
              >
                Cancel the event
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  )
}
